import "./App.scss";
import React, { useEffect, useState } from "react";
import { uploadPlanAdjustment } from "./backendServices";
import {
  getArea,
  convertTo12Hr,
  getAmPm,
  getTimeStamp
} from "./components/functions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";

const adjustDown = [
  "Bakery quiet",
  "We never sell at this time",
  "Don't have enough ingredients",
  "Ingredients not delivered",
  "Food quality issues",
  "Merging waves",
  "Don't have enough staff"
];

const adjustUp = [
  "Bakery busy",
  "We always sell at this time",
  "Splitting into more waves",
  "Sold out",
  "Need to produce more",
  "Online pre-order",
  "Other"
];

interface ForecastEditProps {
  storeName: string | null;
  isFOH: boolean;
  productName: string | null;
  productSKU: string;
  plannerHour: string;
  quantity: number;
  made: number | null;
  handleHideFeedback: () => void;
  handleClearFeedback: () => void;
  showForecastEdit: boolean;
  refreshWavePlanner: () => void;
}

const ForecastEditProps: React.FC<ForecastEditProps> = ({
  storeName,
  isFOH,
  productName,
  productSKU,
  plannerHour,
  quantity,
  made,
  handleHideFeedback,
  handleClearFeedback,
  showForecastEdit,
  refreshWavePlanner
}) => {
  const [adjustedValue, setAdjustedValue] = useState(quantity);
  const [errorText, setErrorText] = useState("");
  const [reason, setReason] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingError, setIsProcessingError] = useState(false);

  let failureAttempts = 0;

  const handleSuccess = () => {
    setErrorText("Thank you, adjustment submitted");
    failureAttempts = 0;
    window.dataLayer.push({
      event: "Plan adjustment",
      bakery: storeName,
      bakery_area: getArea(isFOH),
      product: productName ?? "",
      productSKU: productSKU ?? "",
      value: reason,
      time: getTimeStamp(),
      quantity: quantity
        ? quantity.toString() + " > " + adjustedValue.toString()
        : "0"
    });
    setTimeout(() => {
      handleHideFeedback();
      handleClearFeedback();
      setErrorText("");
      setIsProcessing(false);
      refreshWavePlanner();
    }, 1000);
  };

  const handleFailure = (retryMessage: string) => {
    setErrorText(retryMessage);
    if (failureAttempts < 10) {
      setIsProcessingError(true);
      setTimeout(() => {
        submitFeedback(reason);
      }, 2000);
      failureAttempts++;
      window.dataLayer.push({
        event: "Plan adjustment",
        bakery: storeName,
        bakery_area: getArea(isFOH),
        value: `${retryMessage}: ${failureAttempts}`,
        time: getTimeStamp(),
        quantity: quantity
          ? quantity.toString() + " > " + adjustedValue.toString()
          : "0"
      });
    } else {
      setIsProcessing(false);
      failureAttempts = 0;
      setErrorText("Sorry, please try again");
      setTimeout(() => {
        setErrorText("");
      }, 2000);
      window.dataLayer.push({
        event: "Plan adjustment",
        bakery: storeName,
        bakery_area: getArea(isFOH),
        value: "Total Failure after 10 tries",
        time: getTimeStamp(),
        quantity: quantity
          ? quantity.toString() + " > " + adjustedValue.toString()
          : "0"
      });
    }
  };

  const adjust = (amount: number) => {
    let newAmount = adjustedValue + amount;
    newAmount = newAmount < 0 ? 0 : newAmount;

    setAdjustedValue(newAmount);
  };

  const handleInput = (event: { target: { value: string } }) => {
    let newAmount = parseInt(event.target.value, 10) || 0;
    newAmount = newAmount < 0 ? newAmount * -1 : newAmount;
    setAdjustedValue(newAmount);
  };

  const submitFeedback = async (selectedReason: string) => {
    setReason(selectedReason);

    const response = await uploadPlanAdjustment([
      {
        [`H${plannerHour}`]: adjustedValue,
        ProductId: parseInt(productSKU)
      }
    ]);
    setIsProcessing(true);
    setIsProcessingError(false);
    if (response.status === 200) {
      try {
        response.data.response == "ok"
          ? handleSuccess()
          : handleFailure("Re-trying database");
      } catch (error) {
        setIsProcessing(false);
        failureAttempts = 0;
        // full fail
        console.error(error);
      }
    } else {
      // Wi-fi error
      console.error("Not a 200");
      handleFailure("No Wi-Fi, re-trying");
    }
  };

  useEffect(() => {
    setAdjustedValue(quantity);
  }, [showForecastEdit]);

  return (
    <div
      className={`feedback hlpr-flexbox ${showForecastEdit ? "active" : null}`}
    >
      <div className="feedback__content">
        <div className="hlpr-relative">
          <div className="metric-nav">
            <div className="metric-nav__close">
              {isProcessing ? (
                <div className="loading-overlay__spinner loading-overlay__spinner--black hidden ">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    className="spinner"
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      className="path"
                      fill="none"
                      strokeWidth="6"
                      cx="33"
                      cy="33"
                      r="30"
                    ></circle>
                  </svg>
                </div>
              ) : (
                <FontAwesomeIcon
                  icon={faXmark as IconProp}
                  className="metric-nav__close-icon"
                  onClick={handleHideFeedback}
                />
              )}
            </div>
          </div>
          <h2 className="h2 metric-title">
            {errorText ? (
              <>{errorText}</>
            ) : (
              <>
                Adjustment on {productName}
                {plannerHour == "Totals" ? (
                  " for production today"
                ) : plannerHour ? (
                  <>
                    {" "}
                    at {convertTo12Hr(parseInt(plannerHour))}
                    <small className="color-red">
                      {getAmPm(parseInt(plannerHour))}
                    </small>
                  </>
                ) : null}
              </>
            )}
          </h2>
          <p>Original forecast: {quantity}</p>
          <div className="bakery-table__units bakery-table__units--left-align">
            <button
              className="red-button quantity-button"
              onClick={() => adjust(-5)}
              disabled={isProcessing}
            >
              -5
            </button>
            <button
              className="red-button quantity-button jr-units-steps"
              onClick={() => adjust(-1)}
              disabled={isProcessing}
            >
              -1
            </button>
            <input
              type="number"
              min="0"
              className="units units--input hlpr-input jr-units-type"
              onChange={(event) => handleInput(event)}
              placeholder={adjustedValue.toString()}
              value={adjustedValue.toString()}
              disabled={isProcessing}
            />
            <button
              className="green-button quantity-button"
              onClick={() => adjust(1)}
              disabled={isProcessing}
            >
              +1
            </button>
            <button
              className="red-button quantity-button"
              onClick={() => adjust(5)}
              disabled={isProcessing}
            >
              +5
            </button>
          </div>
          <div className="feedback__sections">
            <p>Select a reason to submit:</p>
            <div className="feedback__options hlpr-flexbox">
              {adjustDown.map((reason) => (
                <button
                  key={reason}
                  className="feedback__option feedback__option--didnt-make"
                  onClick={() => {
                    submitFeedback(`Adjustment - ${reason}`);
                  }}
                  disabled={isProcessing}
                >
                  {reason}
                </button>
              ))}
              {adjustUp.map((reason) => (
                <button
                  key={reason}
                  className="feedback__option feedback__option--made-but"
                  onClick={() => {
                    submitFeedback(`Adjustment - ${reason}`);
                  }}
                  disabled={isProcessing}
                >
                  {reason}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForecastEditProps;
